import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import {
  Inbox, RefreshCw, Mail, Phone, MapPin, Building2, Calendar,
  Sparkles, CheckCircle2, XCircle, Send, Trash2, AlertCircle, Search,
} from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const STATUS_TABS = [
  { key: "",          label: "Alle",         color: "zinc"   },
  { key: "new",       label: "Neu",          color: "amber"  },
  { key: "contacted", label: "Kontaktiert",  color: "blue"   },
  { key: "converted", label: "Konvertiert",  color: "green"  },
  { key: "rejected",  label: "Abgelehnt",    color: "rose"   },
];

const STATUS_BADGE = {
  new:       "bg-amber-500/15 text-amber-300 border border-amber-500/40",
  contacted: "bg-blue-500/15  text-blue-300  border border-blue-500/40",
  converted: "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40",
  rejected:  "bg-rose-500/15  text-rose-300  border border-rose-500/40",
};

export default function AdminInboundLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [salesUsers, setSalesUsers] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("novatus_user") || "null")?.id; }
    catch { return null; }
  })();

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/leads${q}`)
      .then((r) => setLeads(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Leads konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  useEffect(() => {
    // Fetch assignable sales users (admin / sales_director / sales_manager / sales_rep)
    api.get("/users")
      .then((r) => {
        const list = (r.data || []).filter((u) =>
          ["sales_rep", "sales_manager", "sales_director"].includes(u.role)
          && u.status !== "deleted",
        );
        setSalesUsers(list);
      })
      .catch(() => {});
  }, []);

  const userById = useMemo(() => {
    const m = {};
    for (const u of salesUsers) m[u.id] = u;
    return m;
  }, [salesUsers]);

  const filtered = useMemo(() => {
    let arr = leads;
    if (onlyMine && currentUserId) {
      arr = arr.filter((l) => l.assigned_to === currentUserId);
    }
    if (!search.trim()) return arr;
    const s = search.toLowerCase();
    return arr.filter((l) =>
      [l.first_name, l.last_name, l.email, l.phone, l.city, l.zip, l.source]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [leads, search, onlyMine, currentUserId]);

  const myLeadCount = useMemo(() => {
    if (!currentUserId) return 0;
    return leads.filter((l) => l.assigned_to === currentUserId).length;
  }, [leads, currentUserId]);

  const patchStatus = async (id, newStatus) => {
    try {
      const r = await api.patch(`/admin/leads/${id}`, { status: newStatus });
      setLeads((arr) => arr.map((l) => (l.id === id ? r.data : l)));
      toast.success(`Lead → ${newStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  const assignTo = async (id, userId) => {
    try {
      const r = await api.patch(`/admin/leads/${id}`, { assigned_to: userId || "" });
      setLeads((arr) => arr.map((l) => (l.id === id ? r.data : l)));
      const name = userId
        ? `${userById[userId]?.first_name || ""} ${userById[userId]?.last_name || ""}`.trim()
        : "—";
      if (!userId) {
        toast.success("Zuweisung entfernt");
      } else {
        const conv = r.data?._conversion;
        const openOffer = conv?.project_id
          ? {
              label: "Angebot öffnen →",
              onClick: () => window.open(`/projects/${conv.project_id}?tab=offers`, "_blank", "noopener"),
            }
          : undefined;
        if (conv?.created && conv?.offer_number) {
          toast.success(
            `✓ Zugewiesen an ${name} · Angebot ${conv.offer_number} erzeugt`,
            {
              description: "Der Sales-Rep sieht das Draft-Angebot ab jetzt in seinen Projekten.",
              duration: 10000,
              action: openOffer,
            },
          );
        } else if (conv?.reason === "already-converted") {
          toast.success(
            `Zuweisung an ${name} aktualisiert (Angebot ${conv.offer_number || ""} war schon vorhanden)`,
            { duration: 10000, action: openOffer },
          );
        } else if (conv?.error) {
          toast.error(`Zuweisung ok, aber Konvertierung fehlgeschlagen: ${conv.error}`);
        } else {
          toast.success(`Lead zugewiesen an ${name}`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
    }
  };
  const resendEmail = async (id) => {
    try {
      const r = await api.post(`/admin/leads/${id}/resend-email`);
      if (r.data?.ok) toast.success("E-Mail erneut versendet");
      else toast.error(`Versand fehlgeschlagen: ${r.data?.delivery?.error || "unbekannt"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };
  const deleteLead = async (id) => {
    if (!window.confirm("Lead unwiderruflich löschen?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      toast.success("Lead gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const counts = useMemo(() => {
    const c = { new: 0, contacted: 0, converted: 0, rejected: 0 };
    for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
    return c;
  }, [leads]);

  const reporting = useMemo(() => {
    const total = leads.length || 1;
    const touched = counts.contacted + counts.converted + counts.rejected;
    const closed = counts.converted + counts.rejected;
    const contactRate = total ? Math.round((touched / total) * 100) : 0;
    const conversionRate = closed ? Math.round((counts.converted / closed) * 100) : 0;
    const unassigned = leads.filter((l) => !l.assigned_to).length;

    // Per-source breakdown
    const bySource = {};
    for (const l of leads) {
      const src = l.source || "—";
      const b = (bySource[src] ||= { total: 0, converted: 0 });
      b.total += 1;
      if (l.status === "converted") b.converted += 1;
    }
    const sourceRows = Object.entries(bySource)
      .map(([source, b]) => ({
        source,
        total: b.total,
        converted: b.converted,
        rate: b.total ? Math.round((b.converted / b.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    return { contactRate, conversionRate, unassigned, sourceRows };
  }, [leads, counts]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="admin-leads-page">
        <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-xs text-[#00E575] tracking-[0.3em] uppercase mb-1">Admin · Telesales</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Inbox size={28} className="text-[#00E575]" />
              Orientierungsangebote
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Eingehende Leads aus der Telesales-App. Jeder Lead enthält ein automatisch berechnetes Orientierungsangebot.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-leads-refresh"
            className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold flex items-center gap-2"
          >
            <RefreshCw size={16} /> Aktualisieren
          </button>
        </header>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="admin-leads-kpis">
          <KpiCard label="Leads gesamt" value={leads.length} testid="kpi-total" />
          <KpiCard label="Kontaktrate" value={`${reporting.contactRate} %`} hint={`${counts.contacted + counts.converted + counts.rejected} / ${leads.length}`} testid="kpi-contact-rate" color="blue" />
          <KpiCard label="Conversion-Rate" value={`${reporting.conversionRate} %`} hint={`${counts.converted} konvertiert`} testid="kpi-conversion-rate" color="green" />
          <KpiCard label="Nicht zugewiesen" value={reporting.unassigned} hint={reporting.unassigned > 0 ? "❗ jetzt zuweisen" : "alles verteilt"} testid="kpi-unassigned" color={reporting.unassigned > 0 ? "amber" : "zinc"} />
        </div>

        {/* Source breakdown */}
        {reporting.sourceRows.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-4" data-testid="admin-leads-by-source">
            <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-zinc-300 mb-2">Top-Quellen · Conversion-Rate</div>
            <div className="space-y-1.5">
              {reporting.sourceRows.map((s) => (
                <div key={s.source} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-zinc-300 flex-shrink-0 min-w-[80px]">{s.source}</span>
                  <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00E575]" style={{ width: `${s.rate}%` }} />
                  </div>
                  <span className="text-zinc-400 flex-shrink-0">{s.converted}/{s.total}</span>
                  <span className="text-[#00E575] font-bold flex-shrink-0 w-10 text-right">{s.rate}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status-Tabs */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              data-testid={`admin-leads-tab-${t.key || "all"}`}
              className={`shrink-0 px-3.5 h-9 rounded-full text-xs font-semibold border transition ${
                statusFilter === t.key
                  ? "bg-[#00E575] text-[#09090B] border-[#00E575]"
                  : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700"
              }`}
            >
              {t.label}
              {t.key && counts[t.key] != null && (
                <span className="ml-1.5 opacity-75">({counts[t.key]})</span>
              )}
            </button>
          ))}
          {/* Quick-Filter: nur die mir zugewiesenen Leads */}
          {currentUserId && (
            <button
              onClick={() => setOnlyMine(!onlyMine)}
              data-testid="admin-leads-only-mine"
              className={`shrink-0 px-3.5 h-9 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                onlyMine
                  ? "bg-sky-500 text-white border-sky-500"
                  : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-sky-500/60"
              }`}
              title="Zeigt nur Leads, die mir zugewiesen sind"
            >
              👤 Mir zugewiesen
              {myLeadCount > 0 && (
                <span className="opacity-75">({myLeadCount})</span>
              )}
            </button>
          )}
        </div>

        {/* Suche */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="admin-leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, Telefon, E-Mail, PLZ, Ort suchen …"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 h-10 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00E575]"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-400 text-sm font-medium">Lade Leads …</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Inbox size={40} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Keine Leads {statusFilter && `mit Status „${statusFilter}"`} gefunden.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-leads-list">
            {filtered.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                open={openId === lead.id}
                onToggle={() => setOpenId(openId === lead.id ? null : lead.id)}
                onPatchStatus={patchStatus}
                onAssign={assignTo}
                onResend={resendEmail}
                onDelete={deleteLead}
                salesUsers={salesUsers}
                userById={userById}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function LeadRow({ lead, open, onToggle, onPatchStatus, onAssign, onResend, onDelete, salesUsers, userById }) {
  const fullName = [lead.salutation, lead.first_name, lead.last_name].filter(Boolean).join(" ") || "—";
  const o = lead.orientation_offer || {};
  const pkg = o.package || {};
  const emailSent = !!lead.email_sent_at;
  const assignedUser = lead.assigned_to ? userById?.[lead.assigned_to] : null;
  const assignedName = assignedUser
    ? `${assignedUser.first_name || ""} ${assignedUser.last_name || ""}`.trim() || assignedUser.email
    : "";

  return (
    <div
      data-testid={`admin-lead-row-${lead.id}`}
      className={`bg-zinc-900 border-2 rounded-2xl overflow-hidden transition ${
        open ? "border-[#00E575]/40 shadow-[0_0_20px_rgba(0,229,117,0.12)]" : "border-zinc-800"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`font-mono text-[12px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${STATUS_BADGE[lead.status] || ""}`}>
              {lead.status}
            </span>
            {lead.source && (
              <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-zinc-300">
                · {lead.source}
              </span>
            )}
            <span className="font-mono text-[12px] text-zinc-300">
              · {new Date(lead.received_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
            </span>
            {assignedName ? (
              <span className="font-mono text-[12px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700" title="Zugewiesener Sales-User">
                👤 {assignedName}
              </span>
            ) : (
              <span className="font-mono text-[12px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30" title="Noch nicht zugewiesen">
                ⚠ Nicht zugewiesen
              </span>
            )}
          </div>
          <h3 className="font-display text-lg font-bold leading-tight truncate">{fullName}</h3>
          <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-3 flex-wrap">
            {lead.phone && <span className="flex items-center gap-1"><Phone size={12} />{lead.phone}</span>}
            {lead.email && <span className="flex items-center gap-1"><Mail size={12} />{lead.email}</span>}
            {(lead.zip || lead.city) && <span className="flex items-center gap-1"><MapPin size={12} />{[lead.zip, lead.city].filter(Boolean).join(" ")}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">Empfehlung</div>
          <div className="font-display text-base font-bold text-[#00E575]">
            {pkg.brand ? `${pkg.brand} ${pkg.kw} kW` : "—"}
          </div>
          <div className="text-xs font-mono text-zinc-300">
            {pkg.verkaufspreis_brutto ? fmtEur(pkg.verkaufspreis_brutto) : "—"}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-800 px-4 py-4 space-y-4">
          {/* Customer details grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DetailRow label="Wohnfläche" value={lead.living_area_sqm ? `${lead.living_area_sqm} m²` : "—"} />
            <DetailRow label="Personen" value={lead.occupants || "—"} />
            <DetailRow label="Baujahr" value={lead.construction_year || "—"} />
            <DetailRow label="Heizung bisher" value={lead.current_heating || "—"} />
            <DetailRow label="Verbrauch" value={lead.consumption_value ? `${lead.consumption_value} ${lead.consumption_unit || ""}` : "—"} />
            <DetailRow label="Wunsch-Paket" value={lead.package || "—"} />
            <DetailRow
              label="Telefon-Termin"
              value={[lead.appointment_date, lead.appointment_time].filter(Boolean).join(" ") || "—"}
              icon={<Calendar size={11} />}
            />
            <DetailRow label="Adresse" value={[lead.street, lead.zip, lead.city].filter(Boolean).join(", ") || "—"} icon={<Building2 size={11} />} />
            <DetailRow label="Agent" value={lead.agent_name || "—"} />
            <DetailRow label="Externe ID" value={lead.external_id || "—"} mono />
          </div>

          {lead.agent_note && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
              <div className="text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Notiz vom Agent</div>
              <p className="text-sm text-zinc-200">{lead.agent_note}</p>
            </div>
          )}

          {/* Orientation offer card */}
          {pkg.name && (
            <div className="bg-[#0d2a1a]/40 border border-[#00E575]/40 rounded-lg p-3">
              <div className="text-[12px] uppercase tracking-wider text-[#00E575] mb-1 flex items-center gap-1">
                <Sparkles size={11} /> Orientierungsangebot
              </div>
              <div className="text-sm font-semibold">{pkg.name}</div>
              <div className="text-xs text-zinc-400 mt-1">
                Geschätzte Heizlast: <strong className="text-zinc-200">{o.estimated_heat_load_kw} kW</strong> ·
                Abgabepreis: <strong className="text-zinc-200">{fmtEur(pkg.abgabepreis_brutto)}</strong> ·
                Vertriebsmarge: <strong className="text-zinc-200">{o.rep_margin_pct}%</strong>
              </div>
              <div className="text-base font-bold text-[#00E575] mt-1">
                Verkaufspreis (brutto): {fmtEur(pkg.verkaufspreis_brutto)}
              </div>
            </div>
          )}

          {/* Email status */}
          <div className="flex items-center gap-2 text-xs">
            {emailSent ? (
              <span className="flex items-center gap-1 text-[#00E575]"><CheckCircle2 size={12} /> E-Mail versendet ({lead.email_delivery?.to?.join(", ")})</span>
            ) : (
              <span className="flex items-center gap-1 text-amber-300"><AlertCircle size={12} /> E-Mail nicht versendet{lead.email_delivery?.error ? `: ${lead.email_delivery.error}` : ""}</span>
            )}
          </div>

          {/* Assignment row */}
          <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-zinc-800">
            <span className="text-[12px] uppercase tracking-wider text-zinc-300 font-semibold">Zugewiesen an</span>
            <select
              data-testid={`admin-lead-assign-${lead.id}`}
              value={lead.assigned_to || ""}
              onChange={(e) => onAssign(lead.id, e.target.value || null)}
              className="bg-zinc-950 border border-zinc-700 rounded-lg px-2 h-9 text-xs text-zinc-100 focus:border-[#00E575] outline-none"
            >
              <option value="">— Nicht zugewiesen —</option>
              {(salesUsers || []).map((u) => (
                <option key={u.id} value={u.id}>
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email} ({u.role})
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zinc-800">
            <button
              data-testid={`admin-lead-action-contacted-${lead.id}`}
              onClick={() => onPatchStatus(lead.id, "contacted")}
              disabled={lead.status === "contacted"}
              className="px-3 h-9 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/30 text-xs font-semibold disabled:opacity-40"
            >
              Kontaktiert
            </button>
            <button
              data-testid={`admin-lead-action-converted-${lead.id}`}
              onClick={() => onPatchStatus(lead.id, "converted")}
              disabled={lead.status === "converted"}
              className="px-3 h-9 rounded-lg bg-[#00E575]/15 hover:bg-[#00E575]/25 text-[#00E575] border border-[#00E575]/30 text-xs font-semibold disabled:opacity-40"
            >
              <CheckCircle2 size={14} className="inline mr-1" />
              Konvertiert
            </button>
            <button
              data-testid={`admin-lead-action-rejected-${lead.id}`}
              onClick={() => onPatchStatus(lead.id, "rejected")}
              disabled={lead.status === "rejected"}
              className="px-3 h-9 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 text-xs font-semibold disabled:opacity-40"
            >
              <XCircle size={14} className="inline mr-1" />
              Ablehnen
            </button>
            <button
              data-testid={`admin-lead-action-resend-${lead.id}`}
              onClick={() => onResend(lead.id)}
              className="px-3 h-9 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold"
            >
              <Send size={14} className="inline mr-1" />
              E-Mail erneut
            </button>
            <button
              data-testid={`admin-lead-action-delete-${lead.id}`}
              onClick={() => onDelete(lead.id)}
              className="px-3 h-9 rounded-lg bg-zinc-900 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300 text-xs font-semibold ml-auto"
            >
              <Trash2 size={14} className="inline" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, icon, mono }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <div className="text-[12px] uppercase tracking-wider text-zinc-300 w-24 shrink-0 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-zinc-200 truncate ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function KpiCard({ label, value, hint, testid, color = "zinc" }) {
  const colorClasses = {
    zinc:  "border-zinc-800 text-zinc-200",
    blue:  "border-blue-500/30 text-blue-300",
    green: "border-[#00E575]/30 text-[#00E575]",
    amber: "border-amber-500/40 text-amber-300",
  }[color] || "border-zinc-800 text-zinc-200";
  return (
    <div data-testid={testid} className={`bg-zinc-900 border rounded-xl p-3 ${colorClasses}`}>
      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-300 mb-1">{label}</div>
      <div className="text-xl font-display font-bold leading-tight">{value}</div>
      {hint && <div className="text-[12px] text-zinc-300 mt-0.5">{hint}</div>}
    </div>
  );
}
